import React, { Dispatch, ReactNode, SetStateAction, useState } from "react";
import { useSideBar } from "hooks/useSidebar";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { SidebarHeader } from "./components/SidebarHeader";
import { Typography } from "./components/Typography";
import { SidebarFooter } from "./components/SidebarFooter";
import {
  GroupsMenu,
  SidebarMain,
  StyledLogo,
  WrapperSidebarDocumentation,
  menuItemStyles,
  themes,
} from "./styles/stylesSidebar";
import { Icon, Tooltip } from "@material-ui/core";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useLocation } from "react-router-dom";
import { IListSections } from "views/Documentation";
import { IconRenderer } from "utils/renderIcons";
import { generateId } from "../TutorialComponent";

interface ISidebarProps {
  children: ReactNode;
  listSections: IListSections[];
  setMarkDown: Dispatch<SetStateAction<string>>;
}

export const formatTopicForDisplay = (topic: string): string => {
  // Remove os asteriscos de negrito
  const cleanTopic = topic?.replace(/\*\*/g, "");

  // Calcula o nível de indentação com base na quantidade de pontos
  const level = (topic.match(/\./g) || []).length;
  const indentation = " ".repeat(level > 1 ? (level - 1) * 2 : 0);

  return `${indentation}${cleanTopic}`;
};

const SidebarDocumentation: React.FC<ISidebarProps> = ({
  children,
  listSections,
  setMarkDown,
}) => {
  const { t } = useTranslation("sidebar");
  // const location = useLocation();
  const navigate = useNavigate();

  const { collapsedDocumentation, hasImage } = useSideBar();

  const [subMenuActive, setSubMenuActive] = useState("");

  const scrollToSection = (sectionId: string) => {
    const id = generateId(sectionId);

    navigate(`#${id}`, { replace: true });
    const section = document.getElementById(id);

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <WrapperSidebarDocumentation
      rtl={false}
      backgroundScroll={themes["light"].sidebar.backgroundColor}
    >
      <Sidebar
        className="sidebar"
        collapsed={collapsedDocumentation}
        backgroundColor={`transparent`}
        rootStyles={{
          background: `linear-gradient(195deg, ${themes["light"].sidebar.gradientIn}, ${themes["light"].sidebar.gradientOut}) !important`,
          color: "#323232",
        }}
      >
        <GroupsMenu collapsed={collapsedDocumentation}>
          <SidebarMain style={{ marginBottom: "1.5rem", marginTop: "2rem" }}>
            <>
              <div className="groupTitle">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  style={{
                    opacity: collapsedDocumentation ? 0 : 0.7,
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("documentation")}
                </Typography>
              </div>

              <Menu
                menuItemStyles={menuItemStyles(
                  "light",
                  collapsedDocumentation,
                  hasImage,
                )}
              >
                {listSections.map((item, index) => {
                  return (
                    <Tooltip
                      title={
                        collapsedDocumentation
                          ? formatTopicForDisplay(item?.section ?? "")
                          : ""
                      }
                      key={index}
                      placement="right"
                    >
                      <SubMenu
                        icon={
                          item.iconName !== undefined && (
                            <IconRenderer iconName={item.iconName} />
                          )
                        }
                        label={formatTopicForDisplay(item?.section ?? "")}
                        onClick={() => {
                          if (item.section !== subMenuActive) {
                            setMarkDown(item.markdown);
                            setSubMenuActive(item.section);
                            scrollToSection(item.section);
                          } else if (item.section === subMenuActive) {
                            setSubMenuActive("");
                          }
                        }}
                        open={item.section === subMenuActive}
                      >
                        {item.listTopics.map((value, index) => {
                          return (
                            index > 0 && (
                              <MenuItem
                                onClick={() => {
                                  scrollToSection(value);
                                }}
                              >
                                {formatTopicForDisplay(value)}
                              </MenuItem>
                            )
                          );
                        })}
                      </SubMenu>
                    </Tooltip>
                  );
                })}
              </Menu>
            </>
          </SidebarMain>
        </GroupsMenu>
      </Sidebar>

      {children}
    </WrapperSidebarDocumentation>
  );
};

export default SidebarDocumentation;
